import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ArrowRight, X } from "lucide-react";
import { PORTFOLIO } from "@/data/site";

/**
 * Compact horizontal work strip — shows several photos in a row with gold
 * side arrows, page dots and a "View more of our work" button underneath.
 */
export default function WorkStrip() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: "trimSnaps",
  });
  const [snaps, setSnaps] = useState([]);
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    });
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  // Lightbox keyboard controls
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + PORTFOLIO.length) % PORTFOLIO.length);
      if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % PORTFOLIO.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div className="relative mx-auto max-w-6xl px-12 sm:px-14 lg:px-16">
      {/* Carousel */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y gap-3 sm:gap-4">
          {PORTFOLIO.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              aria-label={`Enlarge work photo ${i + 1}`}
              className="img-zoom group relative shrink-0 grow-0 basis-1/2 overflow-hidden rounded-md sm:basis-1/3 lg:basis-1/5"
            >
              <span className="block aspect-[4/5]">
                <img
                  src={item.src}
                  alt={`Golden Touch lash and brow work ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Side arrows */}
      <button
        type="button"
        onClick={scrollPrev}
        aria-label="Previous"
        data-testid="workstrip-prev"
        className="absolute left-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors hover:bg-gold hover:text-onyx sm:h-11 sm:w-11"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        type="button"
        onClick={scrollNext}
        aria-label="Next"
        data-testid="workstrip-next"
        className="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors hover:bg-gold hover:text-onyx sm:h-11 sm:w-11"
      >
        <ChevronRight size={22} />
      </button>

      {/* Dots */}
      <div className="mt-6 flex items-center justify-center gap-2.5">
        {snaps.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => emblaApi && emblaApi.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 w-2 rounded-full border transition-all duration-300 ${
              selected === i ? "border-gold bg-gold" : "border-gold/40 bg-transparent"
            }`}
          />
        ))}
      </div>

      {/* View more button */}
      <div className="mt-6 flex justify-center">
        <Link
          to="/gallery"
          className="btn-gold-outline inline-flex items-center gap-2 px-7 py-3 text-[0.7rem] uppercase tracking-[0.25em]"
        >
          View More of Our Work
          <ArrowRight size={15} />
        </Link>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-onyx/95 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Work image viewer"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center text-ivory hover:text-gold"
          >
            <X size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v - 1 + PORTFOLIO.length) % PORTFOLIO.length); }}
            aria-label="Previous image"
            className="absolute left-3 flex h-12 w-12 items-center justify-center text-ivory hover:text-gold md:left-8"
          >
            <ChevronLeft size={40} />
          </button>
          <img
            src={PORTFOLIO[lightbox].src}
            alt={`Golden Touch work ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] w-auto max-w-full object-contain"
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v + 1) % PORTFOLIO.length); }}
            aria-label="Next image"
            className="absolute right-3 flex h-12 w-12 items-center justify-center text-ivory hover:text-gold md:right-8"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </div>
  );
}
